import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Department, Company } from '../types';
import { departmentsApi, companiesApi } from '../services/api';
import { HelpText, useToast } from '@vmcom/ui';

type EsmDept = {
  esm_id: string;
  name: string;
  domain: string;
  parent: string;
  name_for_site: string;
  phones: string;
  work_time: string;
  address: string;
  already_imported: boolean;
};

type DeptFormState = {
  site_host: string;
  company_id: string;
  esm_id: string;
  domain: string;
  city_nom: string;
  city_gen: string;
  city_dat: string;
  city_acc: string;
  city_ins: string;
  city_pre: string;
  address: string;
  phones: string;
  schedule: string;
};

type ReplaceFormState = {
  old_address: string;
  old_phones: string;
  new_address: string;
  new_phones: string;
  dry_run: boolean;
};

const emptyDeptForm = (companyId?: string): DeptFormState => ({
  site_host: '',
  company_id: companyId || '',
  esm_id: '',
  domain: '',
  city_nom: '',
  city_gen: '',
  city_dat: '',
  city_acc: '',
  city_ins: '',
  city_pre: '',
  address: '',
  phones: '',
  schedule: '',
});

const deptToForm = (d: Department): DeptFormState => ({
  site_host: d.site_host || '',
  company_id: d.company_id || '',
  esm_id: d.esm_id || '',
  domain: d.domain || '',
  city_nom: d.city_nom || '',
  city_gen: d.city_gen || '',
  city_dat: d.city_dat || '',
  city_acc: d.city_acc || '',
  city_ins: d.city_ins || '',
  city_pre: d.city_pre || '',
  address: d.address || '',
  phones: d.phones || '',
  schedule: d.schedule || '',
});

interface DepartmentsSettingsProps {
  activeCompanyId?: string;
}

const DepartmentsSettings: React.FC<DepartmentsSettingsProps> = ({ activeCompanyId }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Department[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filterCompanyId, setFilterCompanyId] = useState<string | undefined>(activeCompanyId);
  const [editing, setEditing] = useState<Department | null>(null);
  const [form, setForm] = useState<DeptFormState>(emptyDeptForm());
  const [replaceDept, setReplaceDept] = useState<Department | null>(null);
  const [replaceForm, setReplaceForm] = useState<ReplaceFormState>({
    old_address: '',
    old_phones: '',
    new_address: '',
    new_phones: '',
    dry_run: true,
  });
  const [replaceLoading, setReplaceLoading] = useState(false);
  const [syncingEsm, setSyncingEsm] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [esmList, setEsmList] = useState<EsmDept[]>([]);
  const [esmLoading, setEsmLoading] = useState(false);
  const [esmSelected, setEsmSelected] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);
  const [skippedImport, setSkippedImport] = useState<{ esm_id: string; reason: string }[] | null>(null);
  const [esmPage, setEsmPage] = useState(0);
  const esmRowsPerPage = 12;

  const companyName = (id?: string | null) => companies.find((c) => c.id === id)?.name;

  const load = async (companyId?: string) => {
    setLoading(true);
    try {
      const [data, companyList] = await Promise.all([departmentsApi.list(companyId), companiesApi.list()]);
      setRows(data);
      setCompanies(companyList);
    } catch {
      toast('Не удалось загрузить департаменты', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilterCompanyId(activeCompanyId);
    load(activeCompanyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId]);

  const handleFilterChange = (companyId: string) => {
    const next = companyId || undefined;
    setFilterCompanyId(next);
    load(next);
  };

  const openCreate = () => {
    setEditing({ id: '', site_host: '', company_id: filterCompanyId, city_nom: '' } as Department);
    setForm(emptyDeptForm(filterCompanyId));
  };

  const openEdit = (r: Department) => {
    setEditing(r);
    setForm(deptToForm(r));
  };

  const patchForm = (patch: Partial<DeptFormState>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async () => {
    if (!form.site_host.trim()) {
      toast('Укажите домен', 'warning');
      return;
    }
    if (!form.city_nom.trim()) {
      toast('Укажите город (им. п.)', 'warning');
      return;
    }
    const payload = {
      ...form,
      company_id: form.company_id || null,
    };
    setLoading(true);
    try {
      if (editing?.id) {
        await departmentsApi.update(editing.id, payload);
        toast('Сохранено', 'success');
      } else {
        await departmentsApi.create(payload);
        toast('Добавлено', 'success');
      }
      setEditing(null);
      await load(filterCompanyId);
      window.dispatchEvent(new CustomEvent('gpt-products-departments-changed'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast(err?.response?.data?.detail || err?.message || 'Ошибка сохранения', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await departmentsApi.remove(deleteTarget.id);
      toast('Удалено', 'success');
      setDeleteTarget(null);
      await load(filterCompanyId);
      window.dispatchEvent(new CustomEvent('gpt-products-departments-changed'));
    } catch {
      toast('Не удалось удалить', 'error');
    }
  };

  const handleSyncEsm = async () => {
    if (!editing?.id) return;
    const esmId = (form.esm_id || '').trim();
    if (!esmId) {
      toast('Укажите и сохраните ID в ЕСМ (1С), затем синхронизируйте', 'warning');
      return;
    }
    setSyncingEsm(true);
    try {
      const res = await departmentsApi.syncEsm(editing.id);
      setForm(deptToForm(res.department));
      toast(
        res.changed_fields.length ? `Обновлено из ЕСМ: ${res.changed_fields.join(', ')}` : 'Данные уже актуальны',
        'success',
      );
      await load(filterCompanyId);
      window.dispatchEvent(new CustomEvent('gpt-products-departments-changed'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast(err?.response?.data?.detail || err?.message || 'Ошибка синхронизации с ЕСМ', 'error');
    } finally {
      setSyncingEsm(false);
    }
  };

  const openImport = async () => {
    setImportOpen(true);
    setEsmSelected([]);
    setEsmPage(0);
    setEsmLoading(true);
    try {
      setEsmList(await departmentsApi.esmList());
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast(err?.response?.data?.detail || err?.message || 'ЕСМ недоступен', 'error');
      setEsmList([]);
    } finally {
      setEsmLoading(false);
    }
  };

  const runImport = async () => {
    if (esmSelected.length === 0) {
      toast('Выберите департаменты', 'warning');
      return;
    }
    setImporting(true);
    try {
      const res = await departmentsApi.esmImport(esmSelected, filterCompanyId, true);
      const parts = [
        res.created.length ? `создано ${res.created.length}` : '',
        res.updated.length ? `обновлено ${res.updated.length}` : '',
        res.skipped.length ? `пропущено ${res.skipped.length}` : '',
      ].filter(Boolean);
      toast(`Импорт из ЕСМ: ${parts.join(', ') || 'нет изменений'}`, 'success');
      if (res.skipped.length) setSkippedImport(res.skipped);
      setImportOpen(false);
      await load(filterCompanyId);
      window.dispatchEvent(new CustomEvent('gpt-products-departments-changed'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      toast(err?.response?.data?.detail || err?.message || 'Ошибка импорта', 'error');
    } finally {
      setImporting(false);
    }
  };

  const toggleEsm = (id: string) => {
    setEsmSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const pagedEsm = esmList.slice(esmPage * esmRowsPerPage, esmPage * esmRowsPerPage + esmRowsPerPage);
  const allEsmPageSelected = pagedEsm.length > 0 && pagedEsm.every((r) => esmSelected.includes(r.esm_id));

  return (
    <Box>
      <HelpText>
        Укажите домен сайта (например <code>shop.spb.ru</code> или <code>example.ru</code>) и формы названия города по
        падежам — они используются при генерации уникальных SEO текстов для каждого сайта.
      </HelpText>

      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }} alignItems="center">
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Добавить
        </Button>
        <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon />} onClick={openImport}>
          Импорт из ЕСМ
        </Button>
        <Button variant="outlined" onClick={() => load(filterCompanyId)} disabled={loading}>
          Обновить
        </Button>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="dept-company-filter">Компания</InputLabel>
          <Select
            labelId="dept-company-filter"
            label="Компания"
            value={filterCompanyId || ''}
            onChange={(e) => handleFilterChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Все компании</em>
            </MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading && !rows.length ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={200}>Сайт</TableCell>
                <TableCell width={160}>Компания</TableCell>
                <TableCell>Город (им.п.)</TableCell>
                <TableCell>Адрес</TableCell>
                <TableCell>Телефоны</TableCell>
                <TableCell align="right" width={220}>
                  Действия
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.site_host}</TableCell>
                  <TableCell>
                    {r.company_id ? (
                      <Chip size="small" color="primary" label={companyName(r.company_id) || r.company_id} />
                    ) : (
                      <Chip size="small" color="warning" label="не привязан" />
                    )}
                  </TableCell>
                  <TableCell>{r.city_nom}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.address}</TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.phones}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редактировать">
                      <IconButton size="small" onClick={() => openEdit(r)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      startIcon={<SwapHorizOutlinedIcon />}
                      disabled={!r.id}
                      onClick={() => {
                        setReplaceDept(r);
                        setReplaceForm({
                          old_address: '',
                          old_phones: '',
                          new_address: r.address || '',
                          new_phones: r.phones || '',
                          dry_run: true,
                        });
                      }}
                    >
                      Контакты в SEO
                    </Button>
                    <Tooltip title="Удалить">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!replaceDept} onClose={() => setReplaceDept(null)} maxWidth="md" fullWidth>
        <DialogTitle>Замена адреса/телефона в SEO категорий: {replaceDept?.site_host || ''}</DialogTitle>
        <DialogContent>
          <HelpText>
            Ищет в Mongo коллекции <code>seo_texts_category</code> по <code>site_host</code> этого департамента и делает
            обычную замену подстрок. Укажите точные фрагменты из старых текстов.
          </HelpText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Старый адрес (фрагмент из текста)"
            margin="normal"
            value={replaceForm.old_address}
            onChange={(e) => setReplaceForm({ ...replaceForm, old_address: e.target.value })}
          />
          <TextField
            fullWidth
            label="Старый телефон (фрагмент из текста)"
            margin="normal"
            value={replaceForm.old_phones}
            onChange={(e) => setReplaceForm({ ...replaceForm, old_phones: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Новый адрес (подставится в текст)"
            margin="normal"
            value={replaceForm.new_address}
            onChange={(e) => setReplaceForm({ ...replaceForm, new_address: e.target.value })}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Новые телефоны"
            margin="normal"
            value={replaceForm.new_phones}
            onChange={(e) => setReplaceForm({ ...replaceForm, new_phones: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={replaceForm.dry_run}
                onChange={(_, v) => setReplaceForm({ ...replaceForm, dry_run: v })}
              />
            }
            label="Только проверка (ничего не записывать)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDept(null)}>Отмена</Button>
          <Button
            variant="contained"
            disabled={replaceLoading}
            onClick={async () => {
              if (!replaceDept?.id) return;
              if (!replaceForm.old_address.trim() && !replaceForm.old_phones.trim()) {
                toast('Укажите старый адрес и/или старый телефон', 'warning');
                return;
              }
              setReplaceLoading(true);
              try {
                const data = await departmentsApi.replaceContactsInCategorySeo(replaceDept.id, {
                  old_address: replaceForm.old_address.trim(),
                  old_phones: replaceForm.old_phones.trim(),
                  new_address: replaceForm.new_address.trim() || undefined,
                  new_phones: replaceForm.new_phones.trim() || undefined,
                  dry_run: replaceForm.dry_run,
                });
                toast(
                  replaceForm.dry_run
                    ? `Проверка: документов с текстом ${data.scanned_with_text}, будет затронуто ${data.updated_documents}, замен ${data.replacement_count}`
                    : `Готово: обновлено документов ${data.updated_documents}, замен подстрок ${data.replacement_count}`,
                  'success',
                );
                if (!replaceForm.dry_run) window.dispatchEvent(new CustomEvent('refreshStats'));
                setReplaceDept(null);
              } catch (e: unknown) {
                const err = e as { response?: { data?: { detail?: string } }; message?: string };
                toast(err?.response?.data?.detail || err?.message || 'Ошибка', 'error');
              } finally {
                setReplaceLoading(false);
              }
            }}
          >
            Выполнить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? 'Редактирование департамента' : 'Новый департамент'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Адрес сайта (домен)"
            margin="normal"
            placeholder="например spb.shop.ru"
            value={form.site_host}
            onChange={(e) => patchForm({ site_host: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="dept-company">Компания</InputLabel>
            <Select
              labelId="dept-company"
              label="Компания"
              value={form.company_id}
              onChange={(e) => patchForm({ company_id: e.target.value })}
            >
              <MenuItem value="">
                <em>Не привязан</em>
              </MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              label="ID департамента в ЕСМ (1С)"
              margin="normal"
              placeholder="например 00090"
              value={form.esm_id}
              onChange={(e) => patchForm({ esm_id: e.target.value })}
              helperText="Ключ синхронизации со справочником учётной системы"
            />
            {editing?.id && (
              <Button
                variant="outlined"
                startIcon={<CloudSyncOutlinedIcon />}
                disabled={syncingEsm}
                onClick={handleSyncEsm}
                sx={{ mb: 1, whiteSpace: 'nowrap' }}
              >
                Синхронизировать
              </Button>
            )}
          </Stack>
          <TextField
            fullWidth
            label="Домен из ЕСМ (для макроса <site>)"
            margin="normal"
            placeholder="например spb.shop.ru"
            value={form.domain}
            onChange={(e) => patchForm({ domain: e.target.value })}
            helperText="Если отличается от адреса сайта. Пусто — в SEO используется адрес сайта."
          />
          {(['city_nom', 'city_gen', 'city_dat', 'city_acc', 'city_ins', 'city_pre'] as const).map((key) => {
            const labels: Record<string, string> = {
              city_nom: 'Город, именительный падеж',
              city_gen: 'Родительный',
              city_dat: 'Дательный',
              city_acc: 'Винительный',
              city_ins: 'Творительный',
              city_pre: 'Предложный («в …»)',
            };
            return (
              <TextField
                key={key}
                fullWidth
                required={key === 'city_nom'}
                label={labels[key]}
                margin="normal"
                value={form[key]}
                onChange={(e) => patchForm({ [key]: e.target.value })}
              />
            );
          })}
          <TextField fullWidth label="Адрес (улица, дом)" margin="normal" value={form.address} onChange={(e) => patchForm({ address: e.target.value })} />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Телефоны"
            margin="normal"
            placeholder="Через запятую или с новой строки"
            value={form.phones}
            onChange={(e) => patchForm({ phones: e.target.value })}
          />
          <TextField
            fullWidth
            label="Режим работы"
            margin="normal"
            placeholder="например: ПН-ПТ 9:00–19:00, СБ 10:00–16:00"
            value={form.schedule}
            onChange={(e) => patchForm({ schedule: e.target.value })}
            helperText="Макрос departaments.schedule в SEO"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Импорт департаментов из ЕСМ (1С)</DialogTitle>
        <DialogContent>
          <HelpText>
            Список из учётной системы. Уже заведённые (по esm_id) обновятся при импорте. Новые создаются с адресом сайта =
            domain из ЕСМ
            {filterCompanyId ? ' и привязываются к выбранной компании' : ''}.
          </HelpText>
          {esmLoading ? (
            <CircularProgress size={28} />
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allEsmPageSelected}
                          indeterminate={!allEsmPageSelected && pagedEsm.some((r) => esmSelected.includes(r.esm_id))}
                          onChange={(_, checked) => {
                            const ids = pagedEsm.map((r) => r.esm_id);
                            setEsmSelected((prev) =>
                              checked
                                ? Array.from(new Set([...prev, ...ids]))
                                : prev.filter((id) => !ids.includes(id)),
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell width={90}>ID (1С)</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell width={160}>Домен</TableCell>
                      <TableCell width={140}>Город</TableCell>
                      <TableCell width={90} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedEsm.map((r) => (
                      <TableRow key={r.esm_id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={esmSelected.includes(r.esm_id)} onChange={() => toggleEsm(r.esm_id)} />
                        </TableCell>
                        <TableCell>{r.esm_id}</TableCell>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>{r.domain}</TableCell>
                        <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name_for_site}</TableCell>
                        <TableCell>{r.already_imported ? <Chip size="small" color="success" label="заведён" /> : null}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={esmList.length}
                page={esmPage}
                onPageChange={(_, p) => setEsmPage(p)}
                rowsPerPage={esmRowsPerPage}
                rowsPerPageOptions={[12]}
                labelRowsPerPage=""
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Отмена</Button>
          <Button variant="contained" disabled={esmSelected.length === 0 || importing} onClick={runImport}>
            Импортировать ({esmSelected.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Удалить департамент?</DialogTitle>
        <DialogContent>{deleteTarget?.site_host}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!skippedImport?.length} onClose={() => setSkippedImport(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Пропущены при импорте</DialogTitle>
        <DialogContent>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {skippedImport?.map((s) => (
              <li key={s.esm_id}>
                <code>{s.esm_id}</code> — {s.reason}
              </li>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkippedImport(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DepartmentsSettings;
